import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Phone, MessageCircle, Gift, CalendarPlus, Star, ArrowLeft, Check, RefreshCw } from 'lucide-react'
import styles from './CSMCustomerProfile.module.css'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { smPost, formatOccasionMonthDay } from './smApi'
import { prettifyOccasionType, emojiForIcon } from './smIcons'

const SM_GET_CUSTOMER_PROFILE = 'sm_get_customer_profile'

const CHANNEL_LABELS = {
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    call: 'Called',
    in_person: 'In Person',
    gift_coupon: 'Gift Coupon',
}

const prettifyChannel = (channel) =>
    CHANNEL_LABELS[channel] || (channel || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const initialsOf = (name) =>
    (name || '')
        .split(/[\s&]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join('')

const buildWhatsAppLink = (phone, name) => {
    const digits = (phone || '').replace(/[^0-9]/g, '')
    const message = encodeURIComponent(`Hi ${(name || '').split(' ')[0]}, wishing you a wonderful day! 🎉`)
    return `https://wa.me/91${digits}?text=${message}`
}

const CSMCustomerProfile = () => {
    const { customerIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [profile, setProfile] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadErrorMessage, setLoadErrorMessage] = useState(null)

    const loadProfile = useCallback(async () => {
        setLoading(true)
        setLoadErrorMessage(null)
        const res = await smPost(authCtx, activeStore, SM_GET_CUSTOMER_PROFILE, {
            client_store_relation_identifier: customerIdentifier,
        })
        if (res?.status === 'success' && res?.response) {
            setProfile(res.response)
        } else {
            setLoadErrorMessage(res?.message || 'Customer not found.')
        }
        setLoading(false)
    }, [authCtx, activeStore, customerIdentifier])

    useEffect(() => {
        if (activeStore?.storeIdentifier && customerIdentifier) loadProfile()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, customerIdentifier])

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading customer profile…</span>
            </div>
        )
    }

    if (loadErrorMessage || !profile) {
        return (
            <div className={styles.page}>
                <Link to="/special-moments/inbox" className={styles.backLink}>
                    <ArrowLeft size={14} /> Back to Celebration Inbox
                </Link>
                <div className={styles.notFound}>{loadErrorMessage || 'Customer not found.'}</div>
            </div>
        )
    }

    const {
        name, phone, is_vip: isVip,
        occasions = [],
        customer_since: customerSince,
        lifetime_spend: lifetimeSpend,
        visits,
        favorite_stylist: favoriteStylist,
        past_wishes: pastWishes = [],
    } = profile

    return (
        <div className={styles.page}>
            <Link to="/special-moments/inbox" className={styles.backLink}>
                <ArrowLeft size={14} /> Back to Celebration Inbox
            </Link>

            <div className={styles.headerCard}>
                <div className={styles.avatar}>{initialsOf(name)}</div>
                <div className={styles.headerText}>
                    <div className={styles.nameRow}>
                        <Link to={`/user/${customerIdentifier}`} className={styles.name}>{name}</Link>
                        {isVip && (
                            <span className={styles.vipBadge}>
                                <Star size={11} fill="currentColor" /> VIP
                            </span>
                        )}
                    </div>

                    {occasions.map((o) => {
                        const detail = o.turning_age != null
                            ? `Turning ${o.turning_age}`
                            : o.years_together != null
                                ? `${o.years_together} Years Together`
                                : null
                        return (
                            <div key={o.occasion_id} className={styles.occasionEntry}>
                                <div className={styles.occasionRow}>
                                    <span>{emojiForIcon(o.icon)} {prettifyOccasionType(o.occasion_type)}</span>
                                    <span className={styles.occasionDot}>•</span>
                                    <span>{formatOccasionMonthDay(o.occasion_date)}</span>
                                    <Link to={`/special-moments/wish/${customerIdentifier}/${o.occasion_id}`} className={styles.occasionWishLink}>
                                        Wish
                                    </Link>
                                </div>
                                {detail && <div className={styles.occasionDetail}>{detail}</div>}
                            </div>
                        )
                    })}
                    {occasions.length === 0 && (
                        <div className={styles.occasionDetail}>No occasions on file yet.</div>
                    )}
                </div>
            </div>

            <div className={styles.statGrid}>
                <div className={styles.statTile}>
                    <div className={styles.statLabel}>Customer Since</div>
                    <div className={styles.statValue}>{customerSince ?? '—'}</div>
                </div>
                <div className={styles.statTile}>
                    <div className={styles.statLabel}>Lifetime Spend</div>
                    <div className={styles.statValue}>₹{(lifetimeSpend ?? 0).toLocaleString('en-IN')}</div>
                </div>
                <div className={styles.statTile}>
                    <div className={styles.statLabel}>Favorite Stylist</div>
                    <div className={styles.statValue}>{favoriteStylist || '—'}</div>
                </div>
                <div className={styles.statTile}>
                    <div className={styles.statLabel}>Visits</div>
                    <div className={styles.statValue}>{visits ?? 0}</div>
                </div>
            </div>

            <div className={styles.sectionLabel}>Past Wishes</div>
            <div className={styles.wishHistory}>
                {pastWishes.length === 0 && (
                    <div className={styles.emptyState}>No wishes sent in previous years yet.</div>
                )}
                {pastWishes.map((w) => (
                    <div key={w.year} className={styles.wishRow}>
                        <span className={styles.wishYear}>{w.year}</span>
                        <span className={styles.wishChannel}>{prettifyChannel(w.channel)}</span>
                        <span className={styles.wishCheck}>
                            {w.status === 'sent' ? <Check size={14} /> : w.status}
                        </span>
                    </div>
                ))}
            </div>

            <div className={styles.sectionLabel}>Quick Actions</div>
            <div className={styles.actionsGrid}>
                <a href={`tel:${phone}`} className={styles.quickAction}>
                    <Phone size={16} />
                    Call
                </a>
                <a
                    href={buildWhatsAppLink(phone, name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.quickAction}
                >
                    <MessageCircle size={16} />
                    WhatsApp
                </a>
                <Link to={`/special-moments/wish/${customerIdentifier}/${occasions[0]?.occasion_id ?? ''}`} className={styles.quickAction}>
                    <Gift size={16} />
                    Gift Coupon
                </Link>
                <Link to="/calendar" className={styles.quickAction}>
                    <CalendarPlus size={16} />
                    Book Appointment
                </Link>
            </div>
        </div>
    )
}

export default CSMCustomerProfile
